import { useEffect, useState, type FormEvent } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";

const navItems = [
  { label: "首页", to: "/" },
  { label: "影视分类", to: "/category" },
  { label: "影视资讯", to: "/news" },
  { label: "关于我们", to: "/about" },
];

export default function SiteHeader() {
  const navigate = useNavigate();
  const location = useLocation();
  const [keyword, setKeyword] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);

  useEffect(() => {
    setMenuOpen(false);
    setMobileSearchOpen(false);
  }, [location.pathname, location.search]);

  const handleSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const query = keyword.trim();
    if (!query) {
      navigate("/category");
      return;
    }
    navigate(`/category?q=${encodeURIComponent(query)}`);
    setMenuOpen(false);
    setMobileSearchOpen(false);
  };

  const navClass = ({ isActive }: { isActive: boolean }) =>
    [
      "relative py-2 text-sm tracking-wide transition-colors",
      isActive
        ? "text-background-50 after:absolute after:-bottom-0.5 after:left-0 after:h-0.5 after:w-full after:rounded-full after:bg-accent-500"
        : "text-primary-200 hover:text-background-50",
    ].join(" ");

  return (
    <header className="sticky top-0 z-40 border-b border-primary-900/60 bg-primary-950/95 backdrop-blur">
      <div className="mx-auto w-full max-w-[1400px] px-4 md:px-6">
        <div className="flex h-16 items-center gap-4 md:h-[76px] md:gap-10">
          <Link to="/" className="flex shrink-0 cursor-pointer items-center gap-3">
            <span className="grid h-9 w-9 place-items-center rounded-md bg-accent-500 text-base font-black text-primary-950 md:h-10 md:w-10">
              姐
            </span>
            <span className="flex flex-col leading-none">
              <span className="text-lg font-bold tracking-wide text-background-50 md:text-xl">姐姐的朋友</span>
              <span className="mt-1 font-label text-[10px] tracking-[0.28em] text-primary-300">SISTER&apos;S FRIEND</span>
            </span>
          </Link>

          <nav className="hidden items-center gap-7 lg:flex">
            {navItems.map((item) => (
              <NavLink key={item.to} to={item.to} end={item.to === "/"} className={navClass}>
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="ml-auto flex items-center gap-2">
            <form
              onSubmit={handleSearch}
              className="hidden items-center gap-2 rounded-md border border-primary-800 bg-primary-900/70 px-3 py-2 transition-colors focus-within:border-accent-500 md:flex"
            >
              <span className="flex h-4 w-4 items-center justify-center text-primary-300">
                <i className="ri-search-line text-base" />
              </span>
              <input
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索影视名称 / 关键词"
                aria-label="搜索影视"
                className="w-40 bg-transparent text-sm text-background-50 placeholder:text-primary-300 focus:outline-none xl:w-52"
              />
              <button
                type="submit"
                className="cursor-pointer whitespace-nowrap rounded-md bg-accent-500 px-3 py-1 font-label text-xs font-semibold text-primary-950 transition-colors hover:bg-accent-400"
              >
                搜索
              </button>
            </form>

            <button
              type="button"
              aria-label="搜索"
              onClick={() => setMobileSearchOpen((open) => !open)}
              className="grid h-10 w-10 cursor-pointer place-items-center rounded-md border border-primary-800 text-background-50 md:hidden"
            >
              <i className={mobileSearchOpen ? "ri-close-line text-xl" : "ri-search-line text-xl"} />
            </button>

            <button
              type="button"
              aria-label="打开菜单"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((open) => !open)}
              className="grid h-10 w-10 cursor-pointer place-items-center rounded-md border border-primary-800 text-background-50 lg:hidden"
            >
              <i className={menuOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"} />
            </button>
          </div>
        </div>
      </div>

      {mobileSearchOpen && (
        <div className="border-t border-primary-900/60 bg-primary-950 px-4 pb-3 pt-3 md:hidden">
          <form
            onSubmit={handleSearch}
            className="flex items-center gap-2 rounded-md border border-primary-800 bg-primary-900/70 px-3 py-2"
          >
            <span className="flex h-4 w-4 items-center justify-center text-primary-300">
              <i className="ri-search-line text-base" />
            </span>
            <input
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="搜索影视名称 / 关键词"
              aria-label="搜索影视"
              className="w-full bg-transparent text-sm text-background-50 placeholder:text-primary-300 focus:outline-none"
            />
            <button
              type="submit"
              className="cursor-pointer whitespace-nowrap rounded-md bg-accent-500 px-3 py-1 font-label text-xs font-semibold text-primary-950"
            >
              搜索
            </button>
          </form>
        </div>
      )}

      {menuOpen && (
        <div className="border-t border-primary-900/60 bg-primary-950 px-4 pb-4 pt-2 lg:hidden">
          <nav className="flex flex-col">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                className={({ isActive }) =>
                  [
                    "flex items-center justify-between border-b border-primary-900/60 py-3 text-sm",
                    isActive ? "text-accent-400" : "text-primary-100",
                  ].join(" ")
                }
              >
                {item.label}
                <span className="flex h-4 w-4 items-center justify-center text-primary-400">
                  <i className="ri-arrow-right-s-line text-base" />
                </span>
              </NavLink>
            ))}
          </nav>
          <div className="mt-4 flex flex-wrap gap-2">
            {["电影", "电视剧", "国产", "海外", "热门推荐"].map((tag) => (
              <Link
                key={tag}
                to={`/category?tag=${tag}`}
                className="cursor-pointer rounded-full border border-primary-800 px-3 py-1.5 text-xs text-primary-200"
              >
                {tag}
              </Link>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}